import type Pusher from 'pusher-js';
import type { Channel } from 'pusher-js';
import { createPusher, isPusherConfigured, PusherConfigurationError } from '@/libs/pusher-client';
import { pusherService } from './pusher-service';

const DEBUG = import.meta.env.DEV;

const SUBSCRIPTION_TIMEOUT_MS = 10000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type EventCallbacks<TComplete, TFailed, TUnknown = never> = {
  onComplete: (data: TComplete) => void;
  onFailed: (data: TFailed) => void;
  onUnknown?: (data: TUnknown) => void;
};

interface BrandEventPusherConfig {
  debugLabel: string;
  completeEvent: string;
  failedEvent: string;
  unknownEvent?: string;
}

/** Handle returned by `subscribe`; the only accepted argument of `unsubscribe`. */
export type BrandEventSubscriptionId = string;

/**
 * Brand-channel event rail shared by several UI surfaces at once.
 *
 * The service is a module singleton, so more than one consumer can be mounted on
 * the same channel (the affiliation email step and the email composer both live
 * behind the invitation outreach instance). Callbacks are therefore held in a
 * per-consumer registry and every event is fanned out to all of them; the
 * channel is refcounted and only torn down once the LAST consumer leaves. A
 * single shared callback slot would let the second subscriber silently steal the
 * first one's events, and a global unsubscribe would cut the other's tracking.
 */
export class BrandEventPusherService<TComplete, TFailed, TUnknown = never> {
  private pusher: Pusher | null = null;
  private channel: Channel | null = null;
  private currentChannelName: string | null = null;
  private readonly consumers = new Map<
    BrandEventSubscriptionId,
    EventCallbacks<TComplete, TFailed, TUnknown>
  >();
  private consumerSequence = 0;
  /** Shared across concurrent subscribers so one channel handshake serves them all. */
  private pendingSubscription: Promise<void> | null = null;

  private readonly log: (...args: unknown[]) => void;
  private readonly error: (...args: unknown[]) => void;

  constructor(private config: BrandEventPusherConfig) {
    this.log = DEBUG ? console.log.bind(console, `[${config.debugLabel}]`) : () => {};
    this.error = DEBUG ? console.error.bind(console, `[${config.debugLabel}]`) : () => {};
  }

  private initPusher(): Pusher {
    if (!this.pusher) {
      this.log('Initializing Pusher instance');
      this.pusher = createPusher({
        forceTLS: true,
        enabledTransports: ['ws', 'wss'],
        channelAuthorization: {
          endpoint: '/pusher/auth',
          transport: 'ajax',
          params: {},
          headers: {},
          customHandler: async (params, callback) => {
            try {
              const auth = await pusherService.authenticate(params.socketId, params.channelName);
              callback(null, auth);
            } catch (err) {
              this.error('Authentication failed:', err);
              callback(err as Error, null);
            }
          },
        },
      });
    }
    return this.pusher;
  }

  /**
   * Register one consumer and resolve once the brand channel is live.
   *
   * @returns the id to pass back to `unsubscribe`. Losing it leaks a callback.
   */
  async subscribe(
    brandId: string,
    callbacks: EventCallbacks<TComplete, TFailed, TUnknown>
  ): Promise<BrandEventSubscriptionId> {
    if (!isPusherConfigured) {
      throw new PusherConfigurationError();
    }

    const channelName = `private-brand-${brandId}`;

    // One channel at a time. A brand switch invalidates every registered
    // consumer, so drop them rather than fanning another brand's events out.
    if (this.currentChannelName !== null && this.currentChannelName !== channelName) {
      this.log('Switching channel, dropping consumers of:', this.currentChannelName);
      this.consumers.clear();
      this.closeChannel();
    }

    this.consumerSequence += 1;
    const subscriptionId = `${this.config.debugLabel}-${this.consumerSequence}`;
    this.consumers.set(subscriptionId, callbacks);
    this.currentChannelName = channelName;

    if (this.channel?.subscribed) {
      this.log('Channel already live, registered consumer:', subscriptionId);
      return subscriptionId;
    }

    if (!this.pendingSubscription) {
      this.pendingSubscription = this.subscribeWithRetries(channelName);
      // Detached from the awaited chain so an unhandled rejection is impossible
      // when every awaiter has already attached its own catch.
      void this.pendingSubscription.catch(() => {});
    }
    const pending = this.pendingSubscription;

    try {
      await pending;
    } catch (err) {
      this.unsubscribe(subscriptionId);
      throw err;
    } finally {
      if (this.pendingSubscription === pending) {
        this.pendingSubscription = null;
      }
    }

    return subscriptionId;
  }

  private async subscribeWithRetries(channelName: string): Promise<void> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        await this.attemptSubscription(channelName);
        return;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error('Unknown subscription error');
        this.error(
          `Subscription attempt ${attempt}/${MAX_RETRY_ATTEMPTS} failed:`,
          lastError.message
        );

        if (attempt < MAX_RETRY_ATTEMPTS) {
          const delayMs = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
          this.log(`Retrying in ${delayMs}ms...`);
          await delay(delayMs);
        }
      }
    }

    throw lastError || new Error('Pusher subscription failed after all retries');
  }

  /** Snapshot the registry: a callback may unsubscribe itself mid-fan-out. */
  private emit(deliver: (callbacks: EventCallbacks<TComplete, TFailed, TUnknown>) => void): void {
    for (const callbacks of [...this.consumers.values()]) {
      try {
        deliver(callbacks);
      } catch (err) {
        this.error('Consumer callback threw:', err);
      }
    }
  }

  private attemptSubscription(channelName: string): Promise<void> {
    const pusher = this.initPusher();

    return new Promise((resolve, reject) => {
      this.log('Subscribing to channel:', channelName);
      this.channel = pusher.subscribe(channelName);

      const timeout = setTimeout(() => {
        this.error('Subscription timeout');
        reject(new Error('Pusher subscription timeout'));
      }, SUBSCRIPTION_TIMEOUT_MS);

      this.channel.bind('pusher:subscription_succeeded', () => {
        clearTimeout(timeout);
        this.log('Successfully subscribed to channel:', channelName);

        this.channel!.bind(this.config.completeEvent, (data: TComplete) => {
          this.log('Received complete event:', data);
          this.emit((callbacks) => callbacks.onComplete(data));
        });

        this.channel!.bind(this.config.failedEvent, (data: TFailed) => {
          this.log('Received failed event:', data);
          this.emit((callbacks) => callbacks.onFailed(data));
        });

        if (this.config.unknownEvent) {
          this.channel!.bind(this.config.unknownEvent, (data: TUnknown) => {
            this.log('Received unknown event:', data);
            this.emit((callbacks) => callbacks.onUnknown?.(data));
          });
        }

        resolve();
      });

      this.channel.bind('pusher:subscription_error', (err: Error) => {
        clearTimeout(timeout);
        this.error('Subscription error:', err);
        reject(err);
      });
    });
  }

  /** Release one consumer. The channel closes only when the registry empties. */
  unsubscribe(subscriptionId: BrandEventSubscriptionId): void {
    if (!this.consumers.delete(subscriptionId)) return;
    this.log('Released consumer:', subscriptionId);
    if (this.consumers.size > 0) return;
    this.closeChannel();
  }

  private closeChannel(): void {
    if (this.channel && this.currentChannelName) {
      this.log('Unsubscribing from channel:', this.currentChannelName);
      this.channel.unbind_all();
      this.pusher?.unsubscribe(this.currentChannelName);
    }
    this.channel = null;
    this.currentChannelName = null;
    this.pendingSubscription = null;
  }

  /** Hard teardown: drops every consumer and the socket. */
  disconnect(): void {
    this.consumers.clear();
    this.closeChannel();
    if (this.pusher) {
      this.log('Disconnecting Pusher');
      this.pusher.disconnect();
      this.pusher = null;
    }
  }
}
